import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { GreenroomState as GS } from '../../../../stores/types/types';
import { getAuthenticatedRequestInit } from '../../../../connection/utils';
import {
	HeaderContainer,
	StyledButton,
	StyledLabel,
	StyledLi,
	StyledSmallButton,
	StyledSpan,
	StyledUl,
} from '../debug-ui';
import { HangupPhone, HangupPhoneByUser } from '../../../../connection/phone';

interface DbCall {
	id: number;
	meeting_id: string;
	gr_user: number | null;
	sip_connection_id: string;
	vonage_call_uuid: string | null;
	opentok_session: string;
	created_at?: string;
}

interface SipStream {
	id: string;
	name: string;
	videoType?: string;
}

interface DebugStatus {
	dbCalls: DbCall[];
	sipStreams: SipStream[];
}

interface ForceCleanResult {
	dbTerminated: number;
	vonageTerminated: number;
	sipDisconnected: number;
}

export default function DebugPstn() {
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meetingId = useSelector((s: GS) => s.SessionReducer.meeting?.meeting_id);
	const apiHost = process.env.REACT_APP_API_HOST;

	const [status, setStatus] = useState<DebugStatus | null>(null);
	const [loading, setLoading] = useState(false);
	const [message, setMessage] = useState<string>('');
	const [hangingUp, setHangingUp] = useState<Set<number>>(new Set());

	const fetchStatus = useCallback(async () => {
		if (!token || !meetingId) return;
		setLoading(true);
		setMessage('');
		try {
			const res = await fetch(`${apiHost}/v2/greenroom/dial-phone/${meetingId}/debug`, {
				...getAuthenticatedRequestInit(token),
			});
			if (res.ok) {
				setStatus(await res.json());
			} else {
				setMessage(`Error fetching status: ${res.status}`);
			}
		} catch (e) {
			setMessage(`Fetch error: ${e}`);
		} finally {
			setLoading(false);
		}
	}, [token, meetingId, apiHost]);

	const hangupCall = async (call: DbCall) => {
		if (!token || !meetingId) return;
		setHangingUp((prev) => new Set(prev).add(call.id));
		const result = call.sip_connection_id
			? await HangupPhone(token, meetingId, call.sip_connection_id)
			: call.gr_user
				? await HangupPhoneByUser(token, meetingId, call.gr_user)
				: { ok: false, message: 'No sip_connection_id or gr_user' };
		setHangingUp((prev) => { const s = new Set(prev); s.delete(call.id); return s; });
		if (!result.ok) {
			setMessage(`Hangup failed: ${result.message}`);
		} else {
			setMessage(`Hung up ${call.sip_connection_id}`);
			fetchStatus();
		}
	};

	const forceClean = async () => {
		if (!token || !meetingId) return;
		setLoading(true);
		setMessage('');
		try {
			const res = await fetch(`${apiHost}/v2/greenroom/dial-phone/${meetingId}/force-clean`, {
				method: 'DELETE',
				...getAuthenticatedRequestInit(token),
			});
			if (res.ok) {
				const result: ForceCleanResult = await res.json();
				setMessage(`Force clean done — DB: ${result.dbTerminated}, Vonage: ${result.vonageTerminated}, SIP disconnected: ${result.sipDisconnected}`);
				fetchStatus();
			} else {
				setMessage(`Force clean failed: ${res.status}`);
			}
		} catch (e) {
			setMessage(`Force clean error: ${e}`);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div style={{ padding: '8px' }}>
			<HeaderContainer>
				<StyledLabel>PSTN Debug</StyledLabel>
				<StyledButton onClick={fetchStatus} disabled={loading}>
					{loading ? 'Loading…' : 'Refresh'}
				</StyledButton>
			</HeaderContainer>

			{message && (
				<div style={{ color: '#A6FF79', fontSize: 11, marginBottom: 8, wordBreak: 'break-all' }}>
					{message}
				</div>
			)}

			{status && (
				<>
					<StyledLabel style={{ display: 'block', marginBottom: 4 }}>
						DB Records ({status.dbCalls.length})
					</StyledLabel>
					{status.dbCalls.length === 0
						? <div style={{ color: '#888', fontSize: 11, marginBottom: 12 }}>No records in gr_meeting_pstn_calls</div>
						: (
							<StyledUl style={{ marginBottom: 12 }}>
								{status.dbCalls.map((call) => (
									<StyledLi key={call.id} style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2, padding: '4px 0' }}>
										<div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
											<StyledSpan style={{ flex: 1, fontSize: 10 }}>
												gr_user: {call.gr_user ?? 'n/a'}
											</StyledSpan>
											<StyledSmallButton
												onClick={() => hangupCall(call)}
												disabled={hangingUp.has(call.id)}
											>
												{hangingUp.has(call.id) ? '…' : 'Hang Up'}
											</StyledSmallButton>
										</div>
										<StyledSpan style={{ fontSize: 9, color: '#888' }}>
											sip: {call.sip_connection_id}
										</StyledSpan>
										<StyledSpan style={{ fontSize: 9, color: '#888' }}>
											vonage: {call.vonage_call_uuid ?? 'none'}
										</StyledSpan>
									</StyledLi>
								))}
							</StyledUl>
						)
					}

					<StyledLabel style={{ display: 'block', marginBottom: 4 }}>
						Active SIP Streams in OpenTok ({status.sipStreams.length})
					</StyledLabel>
					{status.sipStreams.length === 0
						? <div style={{ color: '#888', fontSize: 11, marginBottom: 12 }}>No active SIP streams</div>
						: (
							<StyledUl style={{ marginBottom: 12 }}>
								{status.sipStreams.map((stream) => (
									<StyledLi key={stream.id}>
										<StyledSpan style={{ fontSize: 10 }}>id: {stream.id}</StyledSpan>
										{stream.videoType && (
											<StyledSpan style={{ fontSize: 9, color: '#888' }}>{stream.videoType}</StyledSpan>
										)}
									</StyledLi>
								))}
							</StyledUl>
						)
					}

					{status.sipStreams.length > 0 && status.dbCalls.length === 0 && (
						<div style={{ color: '#ff9900', fontSize: 11, marginBottom: 8 }}>
							SIP streams active but no DB records — these are zombie connections. Use Force Clean to terminate.
						</div>
					)}
				</>
			)}

			<div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
				<StyledButton
					onClick={forceClean}
					disabled={loading}
					style={{ flex: 1, background: '#e74c3c', color: '#fff' }}
				>
					Force Clean (Nuclear)
				</StyledButton>
			</div>
			<div style={{ color: '#888', fontSize: 9, marginTop: 4 }}>
				Terminates all DB records + all active Vonage calls + force-disconnects SIP streams.
			</div>
		</div>
	);
}
